import React, { useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import {
  Award,
  BarChart3,
  Gift,
  Globe,
  Rocket,
  Shield,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';

const ICON_TYPES = [
  Users,
  Sparkles,
  Target,
  Gift,
  TrendingUp,
  Star,
  Rocket,
  Award,
  BarChart3,
  Zap,
  Globe,
  Shield,
];

const ACCENT_PALETTE = [
  { name: 'orange', rgb: '249 115 22' },
  { name: 'amber', rgb: '251 146 60' },
  { name: 'coral', rgb: '234 88 12' },
  { name: 'gold', rgb: '245 158 11' },
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function randomX() {
  const roll = Math.random();
  if (roll < 0.38) return 3 + Math.random() * 24;
  if (roll < 0.76) return 73 + Math.random() * 24;
  return 5 + Math.random() * 90;
}

const MIN_VISIBLE_DEPTH = 0.28;
const VERY_NEAR_THRESHOLD = 0.75;
const MIN_ICONS_PER_SCENE = 5;
const MAX_ICONS_PER_SCENE = 7;
const MIN_ICONS_PER_SCENE_MOBILE = 3;
const MAX_ICONS_PER_SCENE_MOBILE = 5;
const MOBILE_BREAKPOINT = 768;

function getIconCountBounds() {
  if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
    return { min: MIN_ICONS_PER_SCENE_MOBILE, max: MAX_ICONS_PER_SCENE_MOBILE };
  }
  return { min: MIN_ICONS_PER_SCENE, max: MAX_ICONS_PER_SCENE };
}

function randomDepth() {
  const roll = Math.random();
  if (roll < 0.28) return VERY_NEAR_THRESHOLD + Math.random() * 0.1;
  if (roll < 0.68) return 0.55 + Math.random() * 0.3;
  return MIN_VISIBLE_DEPTH + Math.random() * 0.24;
}

function getDepthTier(depth) {
  if (depth >= VERY_NEAR_THRESHOLD) return 'very-near';
  if (depth >= 0.58) return 'near';
  return 'mid';
}

function pickAccent() {
  return ACCENT_PALETTE[Math.floor(Math.random() * ACCENT_PALETTE.length)];
}

function createSceneIcon(segmentTop, segmentHeight) {
  const Icon = ICON_TYPES[Math.floor(Math.random() * ICON_TYPES.length)];
  const localY = 0.06 + Math.random() * 0.88;
  const y = segmentTop + localY * segmentHeight;
  const depth = randomDepth();
  const tier = getDepthTier(depth);
  const driftBase = tier === 'very-near' ? 12 : tier === 'near' ? 8 : 5;
  const drift = driftBase + Math.random() * 4;
  const accent = pickAccent();

  return {
    Icon,
    x: clamp(randomX(), 3, 97),
    y,
    depth,
    accent,
    rotate: -30 + Math.random() * 60,
    delay: Math.random() * 3.5,
    duration: 18 + Math.random() * 10,
    drift,
    floatX: drift * (0.55 + Math.random() * 0.45) * (Math.random() > 0.5 ? 1 : -1),
    floatY: drift * (0.45 + Math.random() * 0.55) * (Math.random() > 0.5 ? 1 : -1),
  };
}

function buildDepthLayers(sceneCount) {
  const segmentHeight = 100 / sceneCount;
  const sceneLayers = [];
  const { min, max } = getIconCountBounds();

  for (let scene = 0; scene < sceneCount; scene += 1) {
    const segmentTop = scene * segmentHeight;
    const targetCount = min + Math.floor(Math.random() * (max - min + 1));
    const sceneIcons = [];

    while (sceneIcons.length < targetCount) {
      const icon = createSceneIcon(segmentTop, segmentHeight);
      if (icon.depth >= MIN_VISIBLE_DEPTH) {
        sceneIcons.push(icon);
      }
    }

    sceneLayers.push(sceneIcons);
  }

  return sceneLayers;
}

function buildOrbs(sceneCount) {
  const sizes = ['sm', 'md', 'lg'];
  const orbCount = Math.max(3, Math.ceil(sceneCount / 2));

  return Array.from({ length: orbCount }, (_, index) => ({
    id: index,
    top: ((index + Math.random() * 0.65) / orbCount) * 100,
    left: `${6 + Math.random() * 82}%`,
    size: sizes[Math.floor(Math.random() * sizes.length)],
    tone: ACCENT_PALETTE[index % ACCENT_PALETTE.length].name,
  }));
}

function normalizeDepth(depth) {
  return clamp(depth, MIN_VISIBLE_DEPTH, 1);
}

function getDepthStyle(depth) {
  const d = normalizeDepth(depth);
  const veryNear = d >= VERY_NEAR_THRESHOLD;
  const near = d >= 0.58;
  const depthProgress = (d - MIN_VISIBLE_DEPTH) / (1 - MIN_VISIBLE_DEPTH);

  const scale = veryNear
    ? 1.1 + depthProgress * 0.62
    : near
      ? 0.72 + depthProgress * 0.38
      : 0.38 + d ** 0.85 * 0.82;

  const opacity = veryNear
    ? 0.58 + d * 0.34
    : near
      ? 0.28 + d * 0.36
      : 0.12 + d * 0.24;

  return {
    scale,
    opacity,
    zIndex: Math.round(d * 100) + (veryNear ? 30 : near ? 12 : 0),
    translateZ: -200 + depthProgress * 280,
  };
}

function getStrokeWidth(depth) {
  const d = normalizeDepth(depth);
  return 1.55 + ((d - MIN_VISIBLE_DEPTH) / (1 - MIN_VISIBLE_DEPTH)) * 0.9;
}

function FloatingIcon({ item, reducedMotion }) {
  const {
    Icon,
    x,
    y,
    depth,
    accent,
    rotate,
    delay,
    duration = 18,
    drift,
    floatX,
    floatY,
  } = item;
  const { scale, opacity, zIndex, translateZ } = getDepthStyle(depth);
  const tier = getDepthTier(depth);
  const strokeWidth = getStrokeWidth(depth);

  return (
    <div
      className={`hero-depth-icon hero-depth-icon--${tier} hero-depth-icon--accent-${accent.name}`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        zIndex,
        opacity,
        '--depth-z': `${translateZ}px`,
        '--icon-scale': scale,
        '--icon-rotate': `${rotate}deg`,
        '--float-drift': `${drift}px`,
        '--float-x': `${floatX}px`,
        '--float-y': `${floatY}px`,
        '--icon-accent-rgb': accent.rgb,
      }}
    >
      <div className="hero-depth-icon__depth">
        <div
          className={`hero-depth-icon__float${
            reducedMotion ? '' : ' hero-depth-icon__float--animated'
          }${tier === 'very-near' && !reducedMotion ? ' hero-depth-icon__float--pulse' : ''}`}
          style={{
            animationDuration: reducedMotion ? undefined : `${duration}s`,
            animationDelay: reducedMotion ? undefined : `${delay}s`,
          }}
        >
          <Icon
            className={`hero-depth-icon__svg hero-depth-icon__svg--${tier}`}
            strokeWidth={strokeWidth}
          />
        </div>
      </div>
    </div>
  );
}

export default function HeroDepthBackground({
  sceneCount = 1,
  className = '',
  fixed = false,
  visibleSceneIndex,
}) {
  const prefersReducedMotion = useReducedMotion();
  const layoutRef = useRef(null);

  if (!layoutRef.current || layoutRef.current.sceneCount !== sceneCount) {
    layoutRef.current = {
      sceneCount,
      sceneLayers: buildDepthLayers(sceneCount),
      orbs: buildOrbs(sceneCount),
    };
  }

  const { sceneLayers, orbs } = layoutRef.current;
  const clampedVisibleIndex =
    visibleSceneIndex == null
      ? null
      : clamp(Math.floor(visibleSceneIndex), 0, sceneCount - 1);
  const depthLayers =
    clampedVisibleIndex == null
      ? sceneLayers.flat()
      : sceneLayers[clampedVisibleIndex] ?? [];

  return (
    <div
      className={`hero-depth-scene pointer-events-none overflow-hidden z-0 ${
        fixed ? 'hero-depth-scene--fixed' : 'hero-depth-scene--track absolute inset-x-0 top-0'
      } ${className}`}
      style={fixed ? undefined : { height: `${sceneCount * 100}dvh` }}
      aria-hidden="true"
    >
      <div className="hero-depth-scene__base hero-depth-scene__base--track" />
      <div className="hero-depth-scene__mesh" />
      <div className="hero-depth-scene__field" />
      <div className="hero-depth-scene__horizon" />
      <div className="hero-depth-scene__vignette" />

      <div className="hero-depth-scene__stage">
        <div className="hero-depth-scene__ambient hero-depth-scene__ambient--track">
          {orbs.map((orb) => (
            <div
              key={`orb-${orb.id}`}
              className="hero-depth-scene__orb-anchor"
              style={{
                top: `${orb.top}%`,
                left: orb.left,
              }}
            >
              <div
                className={`hero-depth-scene__orb hero-depth-scene__orb--${orb.size} hero-depth-scene__orb--${orb.tone}`}
              />
            </div>
          ))}
        </div>

        <div
          className="hero-depth-scene__icons hero-depth-scene__icons--track"
          key={clampedVisibleIndex == null ? 'all-scenes' : `scene-${clampedVisibleIndex}`}
        >
          {depthLayers.map((item, index) => (
            <FloatingIcon
              key={`hero-depth-icon-${clampedVisibleIndex ?? 'all'}-${index}`}
              item={item}
              reducedMotion={prefersReducedMotion}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
